import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { PriceTableChoiceUnit } from '@shared/api/priceTable/priceTable.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────
// 단가표(화환/답례품) 엑셀 다운로드
// 화면 테이블과 동일한 컬럼/값으로 1상품당 1행을 내보낸다.
//
// 프로젝트 표준(커뮤니티 xlsx + file-saver)을 따른다. 이 빌드는 셀 색/굵기 같은
// 스타일을 지원하지 않으므로, 가시성은 열 너비·행 높이·숫자 천단위 포맷·헤더
// 고정·자동 필터로 확보한다. (dashboardExcel/rebateSettlementExcel 과 동일 패턴)
// ─────────────────────────────────────────────

const NUM_FMT = '#,##0';
const HEADER_ROW_PT = 26;
const BODY_ROW_PT = 20;

const typeLabel = (t?: GiftItemInfoType): string =>
  t === GiftItemInfoType.Delivery
    ? '배송형'
    : t === GiftItemInfoType.Coupon
    ? '쿠폰형'
    : '';

/** 한글 2.2칸/영문 1칸 기준 폭 */
function charWidth(s: string): number {
  let w = 0;
  for (const ch of s) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
  return w;
}

/** 컬럼 폭 자동 조정 — 숫자는 천단위 콤마만큼 폭을 더 잡아 잘리지 않게 한다. */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let text = String(cell.v);
      if (typeof cell.v === 'number') {
        const digits = Math.abs(Math.trunc(cell.v)).toString().length;
        text += ','.repeat(Math.max(0, Math.floor((digits - 1) / 3)));
      }
      const w = charWidth(text);
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히. */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 라벨이 금액 컬럼이면 #,##0 숫자 포맷 적용 */
function applyMoneyFormat(ws: XLSX.WorkSheet, moneyLabels: string[]) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const moneyCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && moneyLabels.includes(String(hdr.v))) moneyCols.push(C);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (const C of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = NUM_FMT;
    }
  }
}

/** 헤더 행(1행) 고정 */
function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/** 헤더 행에 자동 필터(정렬/필터 드롭다운) */
function applyAutoFilter(ws: XLSX.WorkSheet) {
  ws['!autofilter'] = { ref: ws['!ref'] ?? 'A1' };
}

function finishSheet(ws: XLSX.WorkSheet, moneyLabels: string[]) {
  autoWidth(ws);
  setRowHeights(ws);
  applyMoneyFormat(ws, moneyLabels);
  freezeHeader(ws);
  applyAutoFilter(ws);
}

/** 원가율(%) 문자열 — 도매가/판매가, 계산 불가면 빈 문자열 */
const pctStr = (priceWhole: number | null, priceRetail: number): string =>
  priceWhole != null && priceRetail > 0
    ? `${((priceWhole / priceRetail) * 100).toFixed(1)}%`
    : '';

/** 숫자 강제변환 (null/빈값은 빈 문자열로 — 엑셀 셀 비움) */
const numOrEmpty = (v: number | null | undefined): number | string =>
  v == null ? '' : Number(v) || 0;

/** 엑셀 입력 행 (PriceTableView 계산 결과와 동일 값) */
export interface PriceTableExcelRow {
  name: string;
  type?: GiftItemInfoType;
  brandName?: string;
  isEnabled?: boolean;
  isSelectable?: boolean;
  choiceUnits?: PriceTableChoiceUnit[];
  priceRetail: number;
  priceWhole: number;
  freedProfit: number;
  costRate: number | null;
}

export interface PriceTableExcelOptions {
  /** 프리드 수익금 컬럼 헤더 */
  profitLabel: string;
  /** 원가율 컬럼 포함 여부 (화면과 동일하게) */
  showCostRate: boolean;
  /** 유형·브랜드 컬럼 포함 여부 (답례품만) */
  showGiftColumns: boolean;
  /** 마리화훼 수수료율 % — FeeRate일 때만, null이면 수수료 컬럼 생략 */
  feeRate?: number | null;
  /** 저장 파일명 (.xlsx 제외) */
  fileName: string;
  /** 시트명 */
  sheetName: string;
}

/** 한 컬럼 값 묶음 (빈칸은 ''로) */
interface RecInput {
  번호: number | string;
  상품명: string;
  판매상태: string;
  유형?: string;
  브랜드?: string;
  판매가: number | string;
  정가?: number | string;
  원가: number | string;
  수수료?: number | string;
  수익금: number | string;
  원가율?: string;
}

/**
 * 단가표를 엑셀로 다운로드한다.
 * 선택형 상품은 **트리(접기/펼치기 그룹)** 로 — 부모행(상품) 아래 선택지를 들여쓴 하위행
 * (아웃라인 레벨1)으로 펼친다. 부모행의 원가/수익금은 비우고, 선택지별 실제 값은 하위행에.
 * (답례품은 유형·브랜드·수수료 컬럼 포함, 화면 필터가 걸린 행 그대로 내보낸다)
 */
export function exportPriceTableExcel(
  rows: PriceTableExcelRow[],
  opts: PriceTableExcelOptions,
) {
  const { profitLabel, showCostRate, showGiftColumns, feeRate, fileName, sheetName } =
    opts;
  const feeLabel = feeRate != null ? `마리화훼 수수료(${feeRate}%)` : null;
  const feeOf = (priceRetail: number): number | string =>
    feeRate != null ? Math.round((priceRetail * feeRate) / 100) : '';
  //* 정가 컬럼은 선택형(choiceUnit)이 하나라도 있을 때만 (그 외엔 전부 빈칸이라 생략)
  const showRegular = rows.some(
    (r) => r.isSelectable && (r.choiceUnits?.length ?? 0) > 0,
  );

  //* 모든 행이 같은 키 순서를 갖도록 고정 빌더 (컬럼 순서/빈칸 보장)
  const buildRec = (v: RecInput): Record<string, unknown> => {
    const rec: Record<string, unknown> = {};
    rec['번호'] = v.번호;
    rec['상품명'] = v.상품명;
    rec['판매상태'] = v.판매상태;
    if (showGiftColumns) {
      rec['유형'] = v.유형 ?? '';
      rec['브랜드'] = v.브랜드 ?? '';
    }
    rec['판매가'] = v.판매가;
    if (showRegular) rec['정가'] = v.정가 ?? '';
    rec['원가'] = v.원가;
    if (feeLabel) rec[feeLabel] = v.수수료 ?? '';
    rec[profitLabel] = v.수익금;
    if (showCostRate) rec['원가율'] = v.원가율 ?? '';
    return rec;
  };

  const data: Record<string, unknown>[] = [];
  const levels: number[] = []; // 각 행의 아웃라인 레벨 (0=상품/일반, 1=선택지)
  let no = 0;

  rows.forEach((row) => {
    const units = row.isSelectable ? row.choiceUnits ?? [] : [];
    const status = row.isEnabled ? '판매중' : '판매중지';
    if (units.length > 0) {
      //* 부모행(상품) — 원가/수익금/원가율은 비우고 선택지별로 펼친다
      no += 1;
      data.push(
        buildRec({
          번호: no,
          상품명: `${row.name} (선택형)`,
          판매상태: status,
          유형: typeLabel(row.type),
          브랜드: row.brandName ?? '',
          판매가: row.priceRetail,
          원가: '',
          수수료: feeOf(row.priceRetail),
          수익금: '',
          원가율: '',
        }),
      );
      levels.push(0);
      //* 하위행(선택지) — 들여쓰기 + 실제 도매가/수익금
      row.choiceUnits?.forEach((cu) => {
        data.push(
          buildRec({
            번호: '',
            상품명: `    └ ${cu.name}`,
            판매상태: '',
            유형: '',
            브랜드: '',
            판매가: '',
            정가: numOrEmpty(cu.priceRegular),
            원가: numOrEmpty(cu.priceWhole),
            수수료: '',
            수익금: numOrEmpty(cu.freedProfit),
            원가율: pctStr(cu.priceWhole, row.priceRetail),
          }),
        );
        levels.push(1);
      });
    } else {
      no += 1;
      data.push(
        buildRec({
          번호: no,
          상품명: row.name,
          판매상태: status,
          유형: typeLabel(row.type),
          브랜드: row.brandName ?? '',
          판매가: row.priceRetail,
          원가: row.priceWhole,
          수수료: feeOf(row.priceRetail),
          수익금: row.freedProfit,
          원가율: row.costRate != null ? `${row.costRate.toFixed(1)}%` : '',
        }),
      );
      levels.push(0);
    }
  });

  const moneyLabels = ['판매가', '원가', profitLabel];
  if (feeLabel) moneyLabels.push(feeLabel);
  if (showRegular) moneyLabels.push('정가');

  const ws = XLSX.utils.json_to_sheet(data);
  finishSheet(ws, moneyLabels);

  //* 트리(아웃라인) — 선택지 행에 레벨1 부여, 부모(요약) 행을 그룹 위로
  if (!ws['!rows']) ws['!rows'] = [];
  const rowInfos = ws['!rows'] as XLSX.RowInfo[];
  levels.forEach((lvl, i) => {
    const R = i + 1; // 헤더가 0행
    rowInfos[R] = { ...(rowInfos[R] ?? {}), level: lvl };
  });
  (ws as XLSX.WorkSheet & { '!outline'?: { above?: boolean; left?: boolean } })[
    '!outline'
  ] = { above: true };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
}
